// 赤波線オーバーレイの DOM 描画
//
// 仕組み:
//   el-input の内部 <textarea> と同じ位置・同じフォントで <div> をかぶせ、
//   テキストを同じように折り返し描画する。lint 対象範囲だけ
//   <span class="proofread-mark"> で囲み、CSS の text-decoration: underline wavy で
//   赤波線を引く。div は pointer-events: none で textarea の操作を邪魔しない。
//   mark span だけ pointer-events: auto にしてクリック可能にする。
//
// Element UI v2 の <el-input type="textarea"> の内部構造:
//   <div class="el-textarea">
//     <textarea class="el-textarea__inner" />
//     <span class="el-input__count"> ... </span>
//   </div>
//   → wrapper は .el-textarea。ここに position: relative を付けて overlay を absolute で重ねる。

const STYLE_PROPS_TO_COPY = [
  'fontFamily',
  'fontSize',
  'fontWeight',
  'fontStyle',
  'lineHeight',
  'letterSpacing',
  'textAlign',
  'textIndent',
  'whiteSpace',
  'wordBreak',
  'wordWrap',
  'overflowWrap',
  'tabSize'
]

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

/**
 * オーバーレイ <div> を生成して wrapper に挿入する。
 * @param {HTMLElement} wrapper textarea の親要素（.el-textarea）
 * @returns {HTMLDivElement}
 */
export function createOverlay(wrapper) {
  const overlay = document.createElement('div')
  overlay.className = 'proofread-overlay'
  overlay.setAttribute('aria-hidden', 'true')
  // 既存の wrapper に position がなければ relative を付ける
  const cs = window.getComputedStyle(wrapper)
  if (cs.position === 'static') {
    wrapper.style.position = 'relative'
  }
  wrapper.appendChild(overlay)
  return overlay
}

/**
 * textarea の computed style をコピーして overlay を同サイズ・同フォントにする。
 */
function syncStyle(overlay, textarea) {
  const cs = window.getComputedStyle(textarea)
  for (const prop of STYLE_PROPS_TO_COPY) {
    overlay.style[prop] = cs[prop]
  }
  // padding / border は textarea と完全一致させないとテキスト位置がズレる
  overlay.style.paddingTop = cs.paddingTop
  overlay.style.paddingRight = cs.paddingRight
  overlay.style.paddingBottom = cs.paddingBottom
  overlay.style.paddingLeft = cs.paddingLeft
  overlay.style.borderTop = `${cs.borderTopWidth} solid transparent`
  overlay.style.borderRight = `${cs.borderRightWidth} solid transparent`
  overlay.style.borderBottom = `${cs.borderBottomWidth} solid transparent`
  overlay.style.borderLeft = `${cs.borderLeftWidth} solid transparent`
  overlay.style.boxSizing = cs.boxSizing

  // 位置・サイズ
  overlay.style.position = 'absolute'
  overlay.style.top = `${textarea.offsetTop}px`
  overlay.style.left = `${textarea.offsetLeft}px`
  overlay.style.width = `${textarea.offsetWidth}px`
  overlay.style.height = `${textarea.offsetHeight}px`

  // 挙動
  overlay.style.pointerEvents = 'none' // mark span だけ auto に戻す
  overlay.style.overflow = 'hidden'
  overlay.style.color = 'transparent' // 本文は透明、下の textarea のテキストを見せる
  overlay.style.background = 'transparent'
  overlay.style.zIndex = '2'
}

/**
 * lint messages を反映して overlay の中身を再描画する。
 * @param {HTMLDivElement} overlay
 * @param {HTMLTextAreaElement} textarea
 * @param {string} text  現在の textarea.value
 * @param {Array}  messages  lintText() の戻り値
 * @param {(msg: object, anchor: HTMLElement) => void} onMarkClick
 */
export function renderOverlay(overlay, textarea, text, messages, onMarkClick) {
  syncStyle(overlay, textarea)

  if (!messages || messages.length === 0) {
    overlay.innerHTML = escapeHtml(text) + '\u200B'
    overlay.scrollTop = textarea.scrollTop
    overlay.scrollLeft = textarea.scrollLeft
    return
  }

  // 範囲情報を正規化: { start, end, msg }
  // 優先順位:
  //   1. msg.range (Worker 側で serializeMessage が設定した表示範囲)
  //   2. msg.fix.range (自動修正可能な範囲)
  //   3. msg.index + 2 (フォールバック: 2文字だけマーク)
  const ranges = []
  for (const msg of messages) {
    let start, end
    if (Array.isArray(msg.range)) {
      ;[start, end] = msg.range
    } else if (msg.fix && Array.isArray(msg.fix.range)) {
      ;[start, end] = msg.fix.range
    } else {
      start = typeof msg.index === 'number' ? msg.index : 0
      end = Math.min(start + 2, text.length)
    }
    if (start < 0 || start >= text.length) continue
    if (end <= start) end = start + 1
    if (end > text.length) end = text.length
    ranges.push({ start, end, msg })
  }

  // 重なりを解決: 開始位置でソートし、重複は最初のものを優先
  ranges.sort((a, b) => a.start - b.start || a.end - b.end)
  const merged = []
  for (const r of ranges) {
    const last = merged[merged.length - 1]
    if (last && r.start < last.end) {
      // 重なりあり → スキップ（最初のものを優先）
      continue
    }
    merged.push(r)
  }

  // DOM 構築
  const parts = []
  let cursor = 0
  const markElements = []
  for (const r of merged) {
    if (r.start > cursor) {
      parts.push(escapeHtml(text.slice(cursor, r.start)))
    }
    const markText = text.slice(r.start, r.end) || ' '
    parts.push(
      `<span class="proofread-mark" data-start="${r.start}" data-end="${r.end}">${escapeHtml(
        markText
      )}</span>`
    )
    cursor = r.end
  }
  if (cursor < text.length) {
    parts.push(escapeHtml(text.slice(cursor)))
  }
  // 末尾改行の折り返しズレを防ぐため zero-width space を付ける
  parts.push('\u200B')

  overlay.innerHTML = parts.join('')
  overlay.scrollTop = textarea.scrollTop
  overlay.scrollLeft = textarea.scrollLeft

  // クリックハンドラ装着（pointer-events: auto に戻す）
  const markNodes = overlay.querySelectorAll('.proofread-mark')
  markNodes.forEach((node, i) => {
    node.style.pointerEvents = 'auto'
    const msg = merged[i] ? merged[i].msg : null
    if (!msg) return
    // モバイルで波線タップ時に textarea の focus を奪うとソフトキーボードが
    // 閉じてしまう。mousedown / touchstart で preventDefault すると
    // タップ直後の blur が発生しない（click は従来どおり発火する）。
    node.addEventListener('mousedown', (e) => {
      e.preventDefault()
    })
    node.addEventListener('click', (e) => {
      e.stopPropagation()
      if (typeof onMarkClick === 'function') {
        onMarkClick(msg, node, merged[i])
      }
    })
    markElements.push({ node, msg, range: merged[i] })
  })

  return markElements
}

/**
 * textarea のスクロールに追従させる。input ハンドラから呼ぶ。
 */
export function syncScroll(overlay, textarea) {
  overlay.scrollTop = textarea.scrollTop
  overlay.scrollLeft = textarea.scrollLeft
}

/**
 * overlay を綺麗に撤去する。
 */
export function destroyOverlay(overlay) {
  if (overlay && overlay.parentNode) {
    overlay.parentNode.removeChild(overlay)
  }
}
